import { apiFirst, apiGetOne, apiGetOneOrNull, apiListAll } from '@/core/api/resources'
import { ApiError } from '@/core/api/http'
import { currentAutosaveScope } from '@/core/api/autosaveScope'
import { descriptionFor } from '@/variants'

export type PersonCoachingRecords = {
  coaching: Record<string, unknown> | null
  documents: Record<string, unknown>[]
  sessions: Record<string, unknown>[]
}

export async function loadPersonOverview(coachingId: string) {
  const scope = currentAutosaveScope()
  const link = await apiFirst('/teilnahme', { coaching_id: coachingId, limit: 1 })
  if (!link) throw new Error("Diese Person ist nicht verfügbar.")
  const [person, participations] = await Promise.all([
    apiGetOne('/teilnehmende/{id}', { id: link.teilnehmende_id }),
    apiListAll('/teilnahme', { teilnehmende_id: link.teilnehmende_id, include: ['aufgabe'] }),
  ])
  const coachings = await Promise.all(participations.map(async (participation) => {
    const id = participation.coaching_id
    try {
      const core = await apiGetOneOrNull('/coaching/{id}', { id }, { include: ['agentur', 'massnahme', 'members'] })
      if (!core) return null
      const description = descriptionFor(core.variant)
      await description.assertPersonAccess?.(id)
      const subtype = description.subtype
      let records: PersonCoachingRecords
      if (subtype) records = await subtype.getPersonRecords(id)
      else {
        const documents = await apiListAll('/dokumente', { coaching_id: id, include: ['signatur', 'sitzung'] })
        const sessions = documents.length ? await apiListAll('/sitzung', { dokument_id__in: documents.map((doc) => doc.id), include: ['anwesenheit'] }) : []
        records = { coaching: core, documents, sessions }
      }

      return { ...records, id, participation, coaching: { ...records.coaching, ...core } as Record<string, unknown> }
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || (error.status === 409 && (error.detailText === 'teilnehmerakte_eingeschraenkt' || error.detailText.startsWith('teilnehmerakte_eingeschraenkt:'))))) return null
      throw error
    }
  }))

  if (scope !== currentAutosaveScope()) throw new Error("Die Organisation hat sich geändert. Bitte öffnen Sie die Person erneut.")
  const accessible = coachings.filter((coaching) => coaching !== null)
  return { person, coachings: accessible, unavailableCoachings: coachings.length - accessible.length }
}
export type PersonOverview = Awaited<ReturnType<typeof loadPersonOverview>>
